import styled, { css } from 'styled-components';

import { TaggedText } from 'components/TaggedText';
import { simpleDateAtTime } from 'lib/date';
import Activity from 'models/Activity';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import NoteMenu from './NoteMenu';

// A mention email links to one note, and the tab scrolls it into view on arrival by id. Exported
// so the two sides cannot drift.
export const noteRowId = (activityId: string) => `activity-${activityId}`;

// The drawer's own padding (ClinicalReviewPanel's SlideoutBody). NotesTab's List bleeds out to the
// panel's edges by exactly this much and pads its content back, so the scrollbar rides the panel
// edge. Declared here, beside the row that shares the number for its own padding, and imported by
// NotesTab so the two cannot drift apart — this file is the leaf, so the dependency runs the only
// way it can.
export const DRAWER_PADDING = 24;

/**
 * One note: author, timestamp, body, and — for the author only — a kebab.
 *
 * No facility line: the mock doesn't show one. The API sends created_by_group and the model
 * exposes `createdByGroup`, so adding it later is a frontend-only change.
 */
export default function NoteRow({
  note,
  workflowId,
  actionId,
  isAuthor,
  focused = false,
}: {
  note: Activity;
  workflowId: string;
  actionId: string;
  isAuthor: boolean;
  /** The note a mention link pointed at — marked so the reader can tell which one wanted them. */
  focused?: boolean;
}) {
  // Absent on every note written before the fan-out shipped, and on every single-facility note
  // after it. Read as 0 so the missing case and the never-written 1 fall through the same guard.
  const fanOutCount: number = note.data.fanOutCount ?? 0;

  return (
    <Row id={noteRowId(note.id)} data-testid='note-row' $focused={focused} data-focused={focused || undefined}>
      <Header>
        <Author data-testid='note-author'>{note.createdBy?.fullName}</Author>
        {note.createdAt && <Timestamp>{simpleDateAtTime(note.createdAt)}</Timestamp>}
        {isAuthor && (
          <MenuCell>
            <NoteMenu note={note} workflowId={workflowId} actionId={actionId} />
          </MenuCell>
        )}
      </Header>
      <Body data-testid='note-body'>
        <TaggedText text={note.data.text ?? ''} $archived={!!note.archived} />
      </Body>
      {/* The author's own copies only. It answers a question only the author has — which of my notes
          went wide — and for anyone else it is a note on their building, which is all they need it to
          be. The count is the one stamped when the note was sent, so this states the act of sending
          rather than what is currently reachable. */}
      {isAuthor && fanOutCount > 1 && (
        <FanOutFooter data-testid='note-fan-out'>Sent to {fanOutCount} facilities</FanOutFooter>
      )}
    </Row>
  );
}

const Row = styled.div<{ $focused?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;

  padding: 20px ${DRAWER_PADDING}px;
  background-color: ${colors.blue5};
  border: 1px solid ${colors.black5};
  border-radius: 8px;

  /* Declared on the base, not inside the focused block, so the highlight animates OUT as well as in
     — NotesTab drops the flag after a couple of seconds and this is what makes that a fade rather
     than a snap. */
  transition:
    background-color 500ms ease-out,
    border-color 500ms ease-out;

  /* Arrived from a mention link: tint the note that asked for the reader, so a thread of
     near-identical cards says which one. Same value as the mention chip in the body (blue10 here,
     --primary-blue-10 there), so the chip reads as part of the band rather than against it. The
     border also switches to blue100 while focused (OLIO-3234 review): every card now rests at
     blue5, so the blue5→blue10 tint alone read too subtly in a thread of many near-identical
     cards — the border gives the arrival a second, sharper signal on top of the tint. */
  ${({ $focused }) =>
    $focused &&
    css`
      background-color: ${colors.blue10};
      border-color: ${colors.blue100};
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Author = styled(BodySmallBold)`
  font-size: 14px;
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

// A third line in the row's existing flex column, so the row's box model does not change. The
// `focused` tint animates against that geometry — an earlier pass added padding here and the text
// slid as the mark faded.
const FanOutFooter = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

const MenuCell = styled.div`
  margin-left: auto;
  position: relative;
`;

// TaggedText already strikes its own text through when archived, so this wrapper only owns the
// type scale — striking here too would be the same rule in two places.
const Body = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${colors.black100};

  /* TaggedText wraps its output in its own BodySmall, which carries the shared type scale.
     Inherit instead, so the row keeps the 14/22 the mock asks for. */
  & > * {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }
`;
